// eslint-disable-next-line @heidi/rules/no-new-legacy-session-data -- pre-existing patients delegate importing only the two frozen linked-session key identities from /contracts (the rule anchors on the first import, hence the placement)
import { isViewingOtherSessionAtom } from '@/atoms/sessionAtoms';
import { selectedReviewMemberAtom } from '@/atoms/teamAtoms';
import {
  invalidateSessionsQuery,
  invalidateTeamMemberSessionsQuery,
} from '@/hooks/query/useQueryCombinedSessions';
import { deleteUnLinkSessionsToPatientProfile } from '@/pages/api/scribeAPIV2';
import { type Dispatch } from '@/store';
import { logger } from '@/utils/logger/logger';
import { sanitizeError } from '@/utils/logger/sanitizeError';
import { toast } from '@heidi/design-system/toast';
import { useTranslations } from '@heidi/runtime/i18n';
// Shared mutation-key identity: the ownership ruling split this hook across
// two packages and both halves declare the same key, from one site.
import { trackPatientUnlinked } from '@/lib/analytics/scribe/patient';
import {
  UNLINK_SESSIONS_TO_PATIENT_PROFILE,
  type PatientProfile,
} from '@heidi/patients/contracts';
import { useMutateUnlinkOwnSessionsToPatientProfile as useMutateUnlinkOwnSessionsToPatientProfileCore } from '@heidi/patients/data';
import {
  GET_LINKED_SESSIONS_BY_PROFILE_ID,
  GET_SESSION_BY_ID,
  GET_TEAM_MEMBER_SESSION_BY_ID,
  GET_TEAM_MEMBER_SESSIONS_BY_PROFILE_ID,
  type QuerySessionResponse,
  type SessionTeamContext,
} from '@heidi/session/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useDispatch } from 'react-redux';
import { useCombinedQueriesTeamGeneralInfo } from '../query/useCombinedQueriesTeamGeneralInfo';
import { GET_TEAM_MEMBER_PATIENT_PROFILES } from '../query/useQueryPatientProfiles';
import { invalidatePatientRecap } from '../query/useQueryPatientRecap';
import { useRBACPermissionCheck } from '../useRBACPermissionCheck';

type Props = {
  sessionIds: string[];
  patientProfile: PatientProfile;
  /** Passed as a query param for session direct-share permission check.
   *  When a session is directly shared, the sharee can process (create/update/link/unlink)
   *  patient profiles on that session by providing its ID here. */
  sessionId?: string;
};

function showUnlinkSuccessToast(
  t: ReturnType<typeof useTranslations>['t'],
  patientProfile: PatientProfile
) {
  toast({
    tone: 'success',
    title: t('toast.sessionUnlinkedFrom', {
      demographicString: patientProfile?.demographic_string,
    }),
    // Default toast width is 400px — shrink to content so short
    // messages don't leave empty space before the close button.
    className: 'w-auto',
  });
}

function trackUnlinkAnalytics(
  dispatch: Dispatch,
  input: Props,
  teamContext?: SessionTeamContext
) {
  if (input.patientProfile?.id) {
    trackPatientUnlinked(
      {
        patient_id: input.patientProfile.id,
        session_ids: input.sessionIds,
      },
      input.patientProfile,
      teamContext
    );
  }
}

const clearPatientProfileUpdater = (
  oldData: QuerySessionResponse | undefined
): QuerySessionResponse | undefined => {
  if (oldData) {
    return {
      ...oldData,
      patient_profile: null,
      session_name: null,
      // Match BE behaviour: `unlink_patient_from_sessions` also nulls
      // extracted_patient_context. The auto-extract suggestion badge + popup
      // are driven solely by the session's extracted_patient_context,
      // independent of the patient link, so without this the suggestions
      // linger on the now-unlinked session until the refetch lands.
      extracted_patient_context: null,
      // Match BE behaviour: the unlink endpoint calls
      // `clear_embedded_patient_fields` which $unsets these two fields
      // on the embedded patient subdoc. Without this, the optimistic
      // update leaves the unlinked patient's name leaking into any
      // consumer reading `session.patient.{name, demographic_details}`
      // (notably the find-or-link combobox) until the refetch lands.
      patient: oldData.patient
        ? {
            ...oldData.patient,
            name: null,
            demographic_details: null,
          }
        : oldData.patient,
    };
  }
  return oldData;
};

export function useMutateUnlinkSessionsToPatientProfile() {
  const { isReviewingOtherUser } = useRBACPermissionCheck();
  const isViewingOtherSession = useAtomValue(isViewingOtherSessionAtom);
  const unlinkOwnSessionsToPatientProfile = useMutateUnlinkOwnSessionsToPatientProfile();
  const unlinkTeamMemberSessionsToPatientProfile =
    useMutateUnlinkTeamMemberSessionsToPatientProfile();
  if (isViewingOtherSession) {
    return unlinkOwnSessionsToPatientProfile;
  }
  if (isReviewingOtherUser) {
    return unlinkTeamMemberSessionsToPatientProfile;
  }
  return unlinkOwnSessionsToPatientProfile;
}

/**
 * App-side delegate for `@heidi/patients/data` (D2.2, ARCFE-1053).
 *
 * Left here: the linked-sessions invalidation, the sessions-list invalidation,
 * the per-session `GET_SESSION_BY_ID` clear (which must keep mirroring what
 * `unlink_patient_from_sessions` nulls server-side), the toast and the
 * `patient_unlinked` analytics — Session's cache and user-visible effects,
 * sequenced by app integration, in their original relative order.
 */
export function useMutateUnlinkOwnSessionsToPatientProfile() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch<Dispatch>();
  const { t } = useTranslations();
  const isViewingOtherSession = useAtomValue(isViewingOtherSessionAtom);

  return useMutateUnlinkOwnSessionsToPatientProfileCore({
    isViewingOtherSession,
    onUnlinked: (input) => {
      void queryClient.invalidateQueries({
        queryKey: [GET_LINKED_SESSIONS_BY_PROFILE_ID, input.patientProfile?.id || ''],
      });
      void invalidateSessionsQuery();

      input.sessionIds.forEach((sessionId) => {
        queryClient.setQueryData<QuerySessionResponse>(
          [GET_SESSION_BY_ID, { sessionId }],
          clearPatientProfileUpdater
        );
      });

      showUnlinkSuccessToast(t, input.patientProfile);
      trackUnlinkAnalytics(dispatch, input);
    },
    onUnlinkFailed: () => {
      // The `logger.error(..., sanitizeError(error))` moved into the package.
      toast({
        tone: 'destructive',
        title: t('toast.backendError'),
      });
    },
  });
}

export function useMutateUnlinkTeamMemberSessionsToPatientProfile() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch<Dispatch>();
  const { teamDetails } = useCombinedQueriesTeamGeneralInfo();
  const teamId = teamDetails?.team.id;
  const selectedReviewMember = useAtomValue(selectedReviewMemberAtom);
  const { t } = useTranslations();

  return useMutation({
    mutationKey: [UNLINK_SESSIONS_TO_PATIENT_PROFILE],
    mutationFn: async (input: Props) => {
      if (input.patientProfile.id && teamId && selectedReviewMember) {
        return await deleteUnLinkSessionsToPatientProfile(
          teamId,
          selectedReviewMember,
          input.patientProfile.id,
          { session_ids: input.sessionIds }
        );
      }
    },
    onSuccess: (_, input) => {
      logger.info('[PatientLink] Session unlinked from patient successfully');
      void queryClient.invalidateQueries({
        queryKey: [
          GET_TEAM_MEMBER_SESSIONS_BY_PROFILE_ID,
          input.patientProfile?.id || '',
          teamId,
          selectedReviewMember,
        ],
      });
      // Same reasoning as the own variant: server-counted session_count must
      // be refetched after an unlink. The 4th key element matches
      // `useQueryTeamMemberPatientProfiles` — `isReviewingOtherUser` (always
      // true on this code path).
      void queryClient.invalidateQueries({
        queryKey: [GET_TEAM_MEMBER_PATIENT_PROFILES, teamId, selectedReviewMember, true],
      });
      // Recap is keyed on profile id only, so the same invalidation
      // refreshes the popover + drawer summary after unlinking.
      invalidatePatientRecap(queryClient, input.patientProfile?.id);
      void invalidateTeamMemberSessionsQuery(teamId, selectedReviewMember);

      input.sessionIds.forEach((sessionId) => {
        queryClient.setQueryData<QuerySessionResponse>(
          [
            GET_TEAM_MEMBER_SESSION_BY_ID,
            { teamId },
            { memberId: selectedReviewMember },
            { sessionId },
          ],
          clearPatientProfileUpdater
        );
      });

      showUnlinkSuccessToast(t, input.patientProfile);
      trackUnlinkAnalytics(
        dispatch,
        input,
        teamId && selectedReviewMember
          ? { teamId, memberId: selectedReviewMember }
          : undefined
      );
    },
    onError: (error) => {
      logger.error(
        '[PatientLink] Failed to unlink session from patient',
        sanitizeError(error)
      );
      toast({
        tone: 'destructive',
        title: t('toast.backendError'),
      });
    },
  });
}
